import React, { useRef, useState } from 'react';
import './projects-page.css';
import { useLocalStorage } from '../../../hooks/use-local-storage';
import { Project, DEFAULT_PROJECTS } from './default-projects';
import { OscillatorSettings } from '../../../synth';
import { DawNote } from '../../../types';
import { createShare, shareUrl } from '../../../share';

interface ProjectFile {
  version: 1;
  projects: Project[];
}

function broadcastLoad(key: string, value: unknown): void {
  const serialized = JSON.stringify(value);
  localStorage.setItem(key, serialized);
  window.dispatchEvent(
    new CustomEvent('synthkitty:storage-update', { detail: { key, value: serialized } }),
  );
}

function readCurrentState(): Omit<Project, 'id' | 'name' | 'savedAt'> {
  const parse = <T,>(key: string, fallback: T): T => {
    try {
      const v = localStorage.getItem(key);
      return v !== null ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  };
  return {
    oscillators: parse<OscillatorSettings[]>('synthkitty:oscillators', []),
    bpm:         parse<number>('synthkitty:bpm', 120),
    measures:    parse<number>('synthkitty:daw-measures', 4),
    notes:       parse<DawNote[]>('synthkitty:daw-notes', []),
    loop:        parse<boolean>('synthkitty:daw-loop', true),
  };
}

function downloadJson(filename: string, data: ProjectFile): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useLocalStorage<Project[]>('synthkitty:projects', DEFAULT_PROJECTS);
  const [nameInput, setNameInput] = useState('');
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [sharedId, setSharedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    const project: Project = {
      id: `proj-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: trimmed,
      savedAt: Date.now(),
      ...readCurrentState(),
    };
    setProjects(prev => [project, ...prev]);
    setNameInput('');
  };

  const handleLoad = (project: Project) => {
    broadcastLoad('synthkitty:oscillators', project.oscillators);
    broadcastLoad('synthkitty:bpm', project.bpm);
    broadcastLoad('synthkitty:daw-measures', project.measures);
    broadcastLoad('synthkitty:daw-notes', project.notes);
    broadcastLoad('synthkitty:daw-loop', project.loop);
    setLoadedId(project.id);
    setTimeout(() => setLoadedId(null), 2000);
  };

  const handleDelete = (id: string) => {
    setProjects(prev => prev.filter(p => p.id !== id));
  };

  const handleShare = async (project: Project) => {
    setSharingId(project.id);
    try {
      const shareId = await createShare(project);
      const url = shareUrl(shareId);
      await navigator.clipboard.writeText(url);
      setSharedId(project.id);
      setTimeout(() => setSharedId(null), 3000);
    } catch {
      // fallback: nothing — error is silent since share is best-effort
    } finally {
      setSharingId(null);
    }
  };

  const handleExportAll = () => {
    downloadJson('synthkitty-projects.json', { version: 1, projects });
  };

  const handleExportOne = (project: Project) => {
    const slug = project.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadJson(`synthkitty-${slug}.json`, { version: 1, projects: [project] });
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string) as ProjectFile;
        if (parsed.version !== 1 || !Array.isArray(parsed.projects)) {
          setImportError('Invalid file format.');
          return;
        }
        setProjects(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const incoming = parsed.projects.filter(p => !existingIds.has(p.id));
          return [...incoming, ...prev];
        });
      } catch {
        setImportError('Could not read file.');
      }
      // Reset so the same file can be re-imported if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="sk-projects-page">
      {/* Toolbar */}
      <div className="sk-projects-toolbar">
        <input
          className="sk-projects-name-input"
          type="text"
          placeholder="Project name…"
          value={nameInput}
          onChange={e => setNameInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
          maxLength={48}
          aria-label="Project name"
        />
        <button
          className="sk-projects-save-btn pixel-font"
          onClick={handleSave}
          disabled={!nameInput.trim()}
        >
          SAVE
        </button>

        <div className="sk-projects-toolbar-divider" />

        <button
          className="sk-projects-io-btn pixel-font"
          onClick={handleExportAll}
          disabled={projects.length === 0}
          title="Export all projects to a JSON file"
        >
          EXPORT ALL
        </button>
        <button
          className="sk-projects-io-btn pixel-font"
          onClick={() => fileInputRef.current?.click()}
          title="Import projects from a JSON file"
        >
          IMPORT
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
      </div>

      {importError && (
        <p className="sk-projects-error ubuntu-font">{importError}</p>
      )}

      {/* Project list */}
      {projects.length === 0 ? (
        <p className="sk-projects-empty">No projects saved yet. ✦</p>
      ) : (
        <ul className="sk-projects-list">
          {projects.map(p => (
            <li key={p.id} className="sk-projects-item">
              <span className="pixel-font sk-projects-item-name">{p.name}</span>
              <span className="sk-projects-item-meta">
                {p.oscillators.length} osc · {p.bpm} BPM · {p.measures} bars · {p.notes.length} notes
              </span>
              <span className="sk-projects-item-date">{formatDate(p.savedAt)}</span>
              <div className="sk-projects-item-actions">
                {loadedId === p.id ? (
                  <span className="pixel-font sk-projects-loaded-badge">LOADED ✓</span>
                ) : (
                  <button
                    className="sk-projects-load-btn pixel-font"
                    onClick={() => handleLoad(p)}
                  >
                    LOAD
                  </button>
                )}
                {sharedId === p.id ? (
                  <span className="pixel-font sk-projects-shared-badge">COPIED ✓</span>
                ) : (
                  <button
                    className="sk-projects-share-btn pixel-font"
                    onClick={() => handleShare(p)}
                    disabled={sharingId === p.id}
                    aria-label={`Share ${p.name}`}
                    title="Copy share link"
                  >
                    {sharingId === p.id ? '…' : 'SHARE'}
                  </button>
                )}
                <button
                  className="sk-projects-export-btn"
                  onClick={() => handleExportOne(p)}
                  aria-label={`Export ${p.name}`}
                  title="Export this project"
                >
                  ↓
                </button>
                <button
                  className="sk-projects-delete-btn"
                  onClick={() => handleDelete(p.id)}
                  aria-label={`Delete ${p.name}`}
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
